import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Guard, SessionResponse } from '@movienight/shared';
import { CrtOverlay, Portrait, TwoShot, usePairLayout } from './Sprite';
import { Typewriter } from './Typewriter';
import { Bribe } from './Bribe';
import { Sing, type SingOutcome } from './Sing';
import { blip, loadMutePreference, setMuted, thunk, unlock } from './audio';
import { resolveNode, type DialogueNode, type Script } from './dialogue';
import {
  checkCode,
  doorBribe,
  doorLogin,
  doorRegister,
  lockoutSeconds,
} from '../lib/api';

/**
 * The visual-novel scene: portraits above a text box.
 *
 * The engine walks nodes; this component renders them and owns the two places
 * where the script hands control back to the app — `input` nodes, which talk
 * to the door API, and `action` nodes, which open the door or raise the arm.
 *
 * **Skip and mute are always visible.** This is a login screen people will
 * pass through hundreds of times, and a mandatory unskippable joke stops being
 * a joke on day three. (DESIGN.md §5)
 */

const ARMANE_SRC = '/assets/dizelasi_Armane.png';
const VERSACE_SRC = '/assets/dizelasi_Versace.png';
const REJECT_SRC = '/assets/dizelasi_ne.png';
const ARMANE_HAT_SRC = '/assets/titovka_Armane.png';
const VERSACE_HAT_SRC = '/assets/titovka_Versace.png';

/** Where each outcome of the audition sends the scene. */
const SING_NODES: Record<SingOutcome, string> = {
  sang: 'pevao',
  silent: 'cutao',
  denied: 'nije_dao_mikrofon',
};

/** Height reserved for the text box, so the sprite scale can be computed. */
const TEXTBOX_PX = 260;

type Resolved = DialogueNode & { text: string };

export function BouncerScene({
  script,
  session,
  onEntered,
  onSceneError,
}: {
  script: Script;
  session: SessionResponse;
  onEntered: () => void;
  /** Any throw in here degrades to the plain form rather than trapping anyone. */
  onSceneError: (reason: string) => void;
}) {
  const layout = usePairLayout(TEXTBOX_PX);

  // Which override blocks are live for this visitor. Order matters: later keys
  // win, so a per-person override beats a per-state one.
  const [overrideKeys, setOverrideKeys] = useState<string[]>(() =>
    session.lateCancels >= 3 ? ['returning_ditcher'] : [],
  );
  const [vars, setVars] = useState<Record<string, string | number>>(() => ({
    broj: session.lateCancels,
  }));

  /**
   * Where the scene opens.
   *
   *  sefe      a guard is on this device's payroll — the 900ms nod, no input
   *  known     already authenticated but never bribed; they typed a password
   *            this browser session, so do not make them do it twice. Straight
   *            to the bill.
   *  stranger  no cookie. Could be a first-timer with a code OR a member on a
   *            new phone, and the scene cannot tell them apart — so `start`
   *            offers both routes and lets them say which they are.
   */
  const startId =
    session.greeting === 'sefe' ? 'sefe' : session.greeting === 'known' ? 'ponuda' : 'start';

  const [nodeId, setNodeId] = useState(startId);
  const [revealKey, setRevealKey] = useState(0);
  const [skipped, setSkipped] = useState(false);
  const [lineDone, setLineDone] = useState(false);
  const [muted, setMutedState] = useState(() => loadMutePreference());
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [alreadyBribed, setAlreadyBribed] = useState<Guard | null>(session.user?.bribedGuard ?? null);

  const node: Resolved | null = useMemo(() => {
    try {
      return resolveNode(script, nodeId, { overrideKeys, vars });
    } catch {
      return null;
    }
    // `revealKey` is in the deps so a pool node picks a new line on re-entry.
  }, [script, nodeId, overrideKeys, vars, revealKey]);

  useEffect(() => {
    if (!node) onSceneError(`nedostaje čvor "${nodeId}"`);
  }, [node, nodeId, onSceneError]);

  const go = useCallback((next: string) => {
    setNodeId(next);
    setRevealKey((k) => k + 1);
    setSkipped(false);
    setLineDone(false);
    setFormError(null);
  }, []);

  // Lockout countdown. Ticks locally; the server remains the authority.
  useEffect(() => {
    if (lockedUntil === null) return;
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [lockedUntil]);

  const remaining = lockedUntil ? Math.max(0, Math.ceil((lockedUntil - now) / 1000)) : 0;
  useEffect(() => {
    if (lockedUntil !== null && remaining === 0) {
      setLockedUntil(null);
      go('trazi_kod');
    }
  }, [lockedUntil, remaining, go]);

  /** Any door rejection carrying a lockout routes to the in-character scene. */
  const handleDoorError = useCallback(
    (err: unknown, fallbackNode: string) => {
      const secs = lockoutSeconds(err);
      if (secs && secs > 0) {
        setLockedUntil(Date.now() + secs * 1000);
        setVars((v) => ({ ...v, minuta: formatCountdown(secs) }));
        go('zakljucano');
        return;
      }
      const message = err instanceof Error ? err.message : 'Ne ide.';
      setFormError(message);
      // Also exposed to the script as {{greska}}, so a node can speak the
      // server's message in the bouncers' voice instead of showing it raw.
      setVars((v) => ({ ...v, greska: message }));
      go(fallbackNode);
    },
    [go],
  );

  // --- auto-advancing nodes (the "Šefe." fast path) ------------------------
  useEffect(() => {
    if (!node?.autoAdvanceMs) return;
    const id = window.setTimeout(() => {
      if (node.action === 'open_door') {
        thunk();
        onEntered();
      } else if (node.goto) {
        go(node.goto);
      }
    }, node.autoAdvanceMs);
    return () => clearTimeout(id);
  }, [node, go, onEntered]);

  if (!node) return null;

  const advance = () => {
    if (!lineDone) {
      // First tap completes the line instantly; a second tap advances.
      setSkipped(true);
      return;
    }
    if (node.action === 'open_door') {
      thunk();
      onEntered();
      return;
    }
    if (node.goto) go(node.goto);
  };

  const showsBribe = node.action === 'offer_bribe';
  const showsSinging = node.action === 'sing';
  const isLocked = node.action === 'retry';
  // `ne` is a single two-shot, so it replaces the pair for that beat. Every
  // other portrait value keeps both bouncers on screen and only changes who
  // is lit — which is what keeps a drag target under each face at all times.
  const rejecting = node.portrait === 'ne';

  return (
    <div
      className="fixed inset-0 flex flex-col justify-end overflow-hidden bg-black"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* --- portraits ---------------------------------------------------
          `items-end` so the sprites sit flush on top of the text box. Any gap
          there reads as a layout mistake rather than as a stage. */}
      <div
        className={`relative flex flex-1 items-end justify-between ${node.shake ? 'animate-[shake_320ms_ease-in-out]' : ''}`}
        onClick={showsBribe || showsSinging || isLocked ? undefined : advance}
      >
        {rejecting ? (
          <div className="relative mx-auto">
            <TwoShot src={REJECT_SRC} layout={layout} />
            <CrtOverlay />
          </div>
        ) : (
          <>
            <Portrait
              guard="armane"
              src={ARMANE_SRC}
              hatSrc={ARMANE_HAT_SRC}
              hat={node.titovka}
              layout={layout}
              dimmed={node.speaker === 'versace'}
            />
            <Portrait
              guard="versace"
              src={VERSACE_SRC}
              hatSrc={VERSACE_HAT_SRC}
              hat={node.titovka}
              layout={layout}
              dimmed={node.speaker === 'armane'}
            />
          </>
        )}
        <CrtOverlay />
      </div>

      {/* --- controls, always visible ----------------------------------
          Right-aligned rather than pinned tight to the corner: each slab casts
          a 6px shadow down and to its right, so `right-4`/`top-4` is what
          leaves them 10px of air off the edge. `gap-4` for the same reason —
          at gap-2 the left slab's shadow lands on the right slab's cap.

          Muted is drawn as the slab held down. The art already ships a
          squished state, a toggle is exactly what that state is for, and a
          permanently pressed button reads faster than a label flipping between
          two words. `aria-pressed` carries the same fact to a screen reader,
          since the visual is the only thing that changes. */}
      <div className="pointer-events-none absolute right-4 top-4 z-50 flex gap-4">
        <button
          type="button"
          aria-pressed={muted}
          aria-label={muted ? 'Ton isključen' : 'Ton uključen'}
          onClick={() => {
            const next = !muted;
            setMuted(next);
            setMutedState(next);
          }}
          className={`slab pointer-events-auto${muted ? ' slab--pressed' : ''}`}
        >
          Ton
        </button>
        <button type="button" onClick={onEntered} className="slab pointer-events-auto">
          Preskoči
        </button>
      </div>

      {/* --- text box --------------------------------------------------- */}
      <div
        className="relative z-40 border-t-2 border-wood-light bg-ink-soft/95 px-5 pb-6 pt-4"
        style={{ minHeight: TEXTBOX_PX }}
        onClick={showsBribe || showsSinging || isLocked || node.input ? undefined : advance}
      >
        <p className="mb-1 text-[11px] uppercase tracking-[0.3em] text-bulb/70">
          {node.speaker === 'obojica'
            ? 'Armane i Versace'
            : node.speaker === 'armane'
              ? 'Armane'
              : 'Versace'}
        </p>

        <div className="min-h-[3.5rem] text-lg leading-snug">
          <Typewriter
            text={isLocked ? node.text.replace(/\{\{minuta\}\}/, formatCountdown(remaining)) : node.text}
            speaker={node.speaker}
            revealKey={revealKey}
            skipped={skipped}
            onDone={() => setLineDone(true)}
          />
        </div>

        {isLocked && (
          <p className="mt-3 font-mono text-2xl text-red-300">{formatCountdown(remaining)}</p>
        )}

        {node.choices && lineDone && !busy && (
          <ul className="mt-4 flex flex-col gap-2">
            {node.choices.map((choice) => (
              <li key={choice.goto}>
                <button
                  type="button"
                  onClick={() => {
                    blip(node.speaker);
                    go(choice.goto);
                  }}
                  className="w-full rounded border border-paper/25 px-3 py-2 text-left text-base"
                >
                  {choice.text}
                </button>
              </li>
            ))}
          </ul>
        )}

        {node.input && lineDone && (
          <DoorInput
            kind={node.input}
            busy={busy}
            error={formError}
            onSubmit={async (values) => {
              setBusy(true);
              setFormError(null);
              try {
                if (node.input === 'code') {
                  const info = await checkCode(values.code ?? '');
                  setVars((v) => ({ ...v, ime: info.intendedForName }));
                  if (info.dialogueKey) setOverrideKeys((k) => [...k, info.dialogueKey!]);
                  setVars((v) => ({ ...v, code: values.code ?? '' }));
                  go(node.onValid!);
                } else if (node.input === 'register') {
                  await doorRegister({
                    code: String(vars.code ?? ''),
                    nickname: values.nickname ?? '',
                    password: values.password ?? '',
                  });
                  go(node.onValid!);
                } else {
                  const res = await doorLogin({
                    nickname: values.nickname ?? '',
                    password: values.password ?? '',
                  });
                  setAlreadyBribed(res.alreadyBribed ?? null);
                  go(node.onValid!);
                }
              } catch (err) {
                handleDoorError(err, node.onInvalid!);
              } finally {
                setBusy(false);
              }
            }}
          />
        )}

        {!node.choices && !node.input && !showsBribe && !showsSinging && !isLocked && lineDone && (
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              className="slab"
              onClick={(e) => {
                // The text box itself advances on tap and this sits inside it,
                // so without stopping the bubble one press would skip a line.
                e.stopPropagation();
                advance();
              }}
            >
              Dalje
            </button>
          </div>
        )}
      </div>

      {/* Keyed on the node so re-entering the audition starts a fresh listen
          rather than reusing a component that has already settled. */}
      {showsSinging && (
        <Sing
          key={nodeId}
          scale={layout.scale}
          bottomPx={TEXTBOX_PX}
          onDone={(outcome) => go(SING_NODES[outcome])}
        />
      )}

      {showsBribe && (
        <Bribe
          alreadyBribed={alreadyBribed}
          onChoose={async (guard) => {
            unlock();
            try {
              const res = await doorBribe(guard);
              if (res.betrayed) {
                go('izdaja');
                return;
              }
            } catch {
              // A failed bribe must never block entry — you are already
              // authenticated by this point; only the session length is at
              // stake, and the server already chose a safe default.
            }
            if (guard === 'armane') go('primljeno_armane');
            else if (guard === 'versace') go('primljeno_versace');
            else go('odbijeno');
          }}
        />
      )}
    </div>
  );
}

function formatCountdown(totalSec: number): string {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** The one place the scene collects credentials. Kept dumb on purpose. */
function DoorInput({
  kind,
  busy,
  error,
  onSubmit,
}: {
  kind: 'code' | 'credentials' | 'register';
  busy: boolean;
  error: string | null;
  onSubmit: (values: { code?: string; nickname?: string; password?: string }) => void;
}) {
  const [code, setCode] = useState('');
  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const first = useRef<HTMLInputElement>(null);

  useEffect(() => {
    first.current?.focus();
  }, [kind]);

  return (
    <form
      className="mt-4 flex flex-col gap-2"
      onClick={(e) => e.stopPropagation()}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ code, nickname, password });
      }}
    >
      {kind === 'code' && (
        <input
          ref={first}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="KOD"
          autoCapitalize="characters"
          autoComplete="one-time-code"
          spellCheck={false}
          className="rounded border border-paper/30 bg-transparent px-3 py-2 font-mono text-lg tracking-[0.25em]"
        />
      )}

      {kind !== 'code' && (
        <>
          <input
            ref={first}
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="ime"
            autoComplete={kind === 'register' ? 'nickname' : 'username'}
            className="rounded border border-paper/30 bg-transparent px-3 py-2"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="šifra"
            autoComplete={kind === 'register' ? 'new-password' : 'current-password'}
            className="rounded border border-paper/30 bg-transparent px-3 py-2"
          />
        </>
      )}

      {error && <p className="text-sm text-red-300">{error}</p>}

      <button type="submit" disabled={busy} className="slab self-end">
        {busy ? '…' : 'OK'}
      </button>
    </form>
  );
}
